/**
 * client/src/lib/prf-key-derivation/index.ts — WebAuthn PRF → Vault KEK
 * derivation.
 *
 * The PRF extension on a WebAuthn assertion returns a 32-byte pseudorandom
 * output bound to the authenticator and origin.  We use this as input key
 * material (IKM) for HKDF-SHA256 to derive the 256-bit vault Key Encryption
 * Key (KEK).
 *
 * Security invariants (L0-6 / L0-9 / Rule 11):
 *   - HKDF is the ONLY approved KEK derivation path (no passwords)
 *   - prfOutput is atomicWipe()'d immediately after KEK is derived (L0-9)
 *   - The derived KEK is never stored in plaintext — it is wrapped with
 *     AES-256-GCM and stored on the server as `vaultKekEncrypted`
 *   - No password string or PBKDF2 anywhere in this module (L0-6)
 *
 * Usage:
 *   1. Caller receives prfOutput (Uint8Array) from the WebAuthn assertion
 *   2. Call derivePrfVaultKek(prfOutput, prfSalt)
 *   3. The function wipes prfOutput and returns the 32-byte KEK
 *   4. Caller uses KEK, then wipes it with atomicWipe()
 *
 * Split from the original single-file `prf-key-derivation.ts` under the
 * 300-line modularity rule (subdirectory + barrel pattern) — see
 * `question-text-key.ts` for the bespoke-capture QTK derivation
 * (`deriveQuestionTextKey`), which reuses `atomicWipe`/`hexToBytes` from
 * this file. The barrel `client/src/lib/prf-key-derivation.ts` re-exports
 * both; all existing imports remain valid.
 *
 * (NIST CSF PR.DS-01 / NIST SP 800-108r1 HKDF)
 */

import { hkdf }        from "@noble/hashes/hkdf";
import { sha256 }      from "@noble/hashes/sha256";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** HKDF context label for vault KEK derivation — must match on all clients */
const VAULT_KEK_INFO = "strem-vault-kek-v1";

/** HKDF output length: 256 bits for AES-256-GCM */
const KEK_LENGTH_BYTES = 32;

// ---------------------------------------------------------------------------
// atomicWipe (client-side) — Rule 3 / L0-9
//
// JavaScript has no guaranteed memory zeroing (GC may copy memory), but
// overwriting the buffer content prevents the plaintext from being readable
// at the ArrayBuffer level, limiting exposure to GC artefacts in hot paths.
// ---------------------------------------------------------------------------

/**
 * Overwrite a Uint8Array with cryptographically random bytes, then zero it.
 * Caller MUST null the reference after calling this.
 *
 * Note: Due to JavaScript's GC semantics, this cannot fully guarantee
 * the buffer is purged from all memory.  However, it eliminates the
 * plaintext from the original allocation.  (Rule 3 / L0-9)
 */
export function atomicWipe(buffer: Uint8Array): void {
  if (!buffer || buffer.length === 0) return;
  // Pass 1: overwrite with random bytes
  crypto.getRandomValues(buffer);
  // Pass 2: zero
  buffer.fill(0);
}

// ---------------------------------------------------------------------------
// derivePrfVaultKek — the sole approved KEK derivation path (L0-6)
// ---------------------------------------------------------------------------

/**
 * Derive the 256-bit vault KEK from the WebAuthn PRF output.
 *
 * @param prfOutput  Raw 32-byte PRF output from the WebAuthn assertion.
 *                   THIS BUFFER IS WIPED AFTER THE CALL (L0-9).
 *                   Do not hold any other references to it.
 *
 * @param prfSalt    The 32-byte hex PRF eval input (from server profile).
 *                   Used as the HKDF salt to domain-separate the KEK.
 *
 * @returns          32-byte vault KEK (Uint8Array).
 *                   Caller is responsible for wiping this with atomicWipe()
 *                   after use.
 */
export function derivePrfVaultKek(
  prfOutput: Uint8Array,
  prfSalt:   string
): Uint8Array {
  if (prfOutput.length !== 32) {
    // Wipe before throwing so the partial output doesn't linger
    atomicWipe(prfOutput);
    throw new Error(
      `[prf-key-derivation] PRF output must be exactly 32 bytes, got ${prfOutput.length}`
    );
  }

  const saltBytes = hexToBytes(prfSalt);
  const infoBytes = new TextEncoder().encode(VAULT_KEK_INFO);

  let kek: Uint8Array;
  try {
    kek = hkdf(sha256, prfOutput, saltBytes, infoBytes, KEK_LENGTH_BYTES);
  } finally {
    // L0-9: atomicWipe prfOutput immediately after KEK is derived, no matter what
    atomicWipe(prfOutput);
  }

  return kek;
}

// ---------------------------------------------------------------------------
// extractPrfOutput — pull the PRF result from a WebAuthn assertion
// ---------------------------------------------------------------------------

/**
 * Extract the PRF output from a WebAuthn authentication extension result.
 *
 * The PRF extension result looks like:
 * ```
 * { prf: { results: { first: ArrayBuffer } } }
 * ```
 *
 * Returns null if the authenticator did not support the PRF extension
 * or if the extension was not evaluated (e.g. registration ceremony).
 */
export function extractPrfOutput(
  extensionResults: AuthenticationExtensionsClientOutputs | undefined
): Uint8Array | null {
  if (!extensionResults) return null;

  // The PRF extension result is typed as `prf?: { results?: { first?: ArrayBuffer } }`
  // in the WebAuthn spec but browsers may use slightly different shapes.
  const prf = (extensionResults as Record<string, unknown>).prf as
    | { results?: { first?: ArrayBuffer }; enabled?: boolean }
    | undefined;

  if (!prf?.results?.first) return null;

  return new Uint8Array(prf.results.first);
}

// ---------------------------------------------------------------------------
// wrapKekWithPrf — encrypt the vault KEK for server storage
// ---------------------------------------------------------------------------

/**
 * Wrap the vault KEK for storage on the server.
 *
 * We encrypt the KEK with AES-256-GCM using the PRF output as the wrapping key.
 * This means the server stores an encrypted blob that only the user's hardware
 * token can unlock (the PRF output never leaves the authenticator).
 *
 * @param kek         The 32-byte vault KEK to wrap.
 * @param prfOutput   The 32-byte PRF output used as the AES key.
 *                    WIPED AFTER USE (L0-9).
 *
 * @returns           Base64url-encoded `iv:ciphertext:tag` string for server storage.
 */
export async function wrapKekWithPrf(
  kek:       Uint8Array,
  prfOutput: Uint8Array
): Promise<string> {
  if (kek.length !== 32 || prfOutput.length !== 32) {
    atomicWipe(prfOutput);
    throw new Error("[prf-key-derivation] KEK and PRF output must each be 32 bytes.");
  }

  let wrapped: string;
  try {
    const aesKey = await crypto.subtle.importKey(
      "raw",
      prfOutput as unknown as BufferSource,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt"]
    );

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertextBuf = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      aesKey,
      kek as unknown as BufferSource
    );

    // ciphertextBuf contains ciphertext + 16-byte GCM tag concatenated
    const ciphertextWithTag = new Uint8Array(ciphertextBuf);
    const ciphertext = ciphertextWithTag.slice(0, ciphertextWithTag.length - 16);
    const tag        = ciphertextWithTag.slice(ciphertextWithTag.length - 16);

    wrapped = [
      bytesToHex(iv),
      bytesToHex(ciphertext),
      bytesToHex(tag),
    ].join(":");
  } finally {
    atomicWipe(prfOutput);
  }

  return wrapped;
}

/**
 * Unwrap the vault KEK using the PRF output.
 *
 * @param wrappedKek  Base64url `iv:ciphertext:tag` string from server.
 * @param prfOutput   The 32-byte PRF output (wrapping key). WIPED AFTER USE.
 *
 * @returns           The 32-byte vault KEK.
 */
export async function unwrapKekWithPrf(
  wrappedKek: string,
  prfOutput:  Uint8Array
): Promise<Uint8Array> {
  const parts = wrappedKek.split(":");
  if (parts.length !== 3) {
    atomicWipe(prfOutput);
    throw new Error("[prf-key-derivation] Invalid wrapped KEK format. Expected iv:ciphertext:tag.");
  }

  const [ivHex, ciphertextHex, tagHex] = parts;
  const iv          = hexToBytes(ivHex);
  const ciphertext  = hexToBytes(ciphertextHex);
  const tag         = hexToBytes(tagHex);

  // Reconstruct ciphertext+tag as SubtleCrypto expects them concatenated
  const ciphertextWithTag = new Uint8Array(ciphertext.length + tag.length);
  ciphertextWithTag.set(ciphertext);
  ciphertextWithTag.set(tag, ciphertext.length);

  let kek: Uint8Array;
  try {
    const aesKey = await crypto.subtle.importKey(
      "raw",
      prfOutput as unknown as BufferSource,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    );

    const plaintextBuf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv as unknown as BufferSource, tagLength: 128 },
      aesKey,
      ciphertextWithTag as unknown as BufferSource
    );

    kek = new Uint8Array(plaintextBuf);
  } catch {
    atomicWipe(prfOutput);
    throw new Error("[prf-key-derivation] KEK decryption failed — PRF output may be incorrect.");
  } finally {
    atomicWipe(prfOutput);
  }

  return kek;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error(`[prf-key-derivation] Invalid hex string length: ${hex.length}`);
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

/**
 * Is this the server's PRF eval salt — exactly 32 bytes of hex?
 *
 * Lives here, next to `hexToBytes`, because it exists to guard `hexToBytes`:
 * that function throws on an odd-length string, and several callers decode the
 * salt OUTSIDE a try/finally that owns a live PRF buffer, so the throw would
 * escape past their L0-9 wipe. Length-checking first makes the decode
 * infallible for them.
 *
 * Stricter than `isHexString` (vault-field-crypto/hex.ts) on purpose: that one
 * is length-agnostic and accepts `""`, so it cannot pin 32 bytes. Also rejects
 * a non-hex string of even length, which `hexToBytes` would otherwise decode
 * silently to zero bytes — a degenerate salt is worse than a rejected one.
 */
export function isPrfSalt(value: string): boolean {
  return /^[0-9a-f]{64}$/i.test(value);
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}
